<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const shouldBeWebhookUrl = (value = '') =>
  value ? value.startsWith('http') : true;

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      channelName: '',
      webhookUrl: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    isWhatsappApiChannel() {
      return this.$route.params.sub_page === 'whatsapp_api';
    },
    channelType() {
      return this.isWhatsappApiChannel ? 'whatsapp_api' : 'api';
    },
    headerTitle() {
      return this.isWhatsappApiChannel
        ? this.$t('INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.TITLE')
        : this.$t('INBOX_MGMT.ADD.API_CHANNEL.TITLE');
    },
    headerDescription() {
      return this.isWhatsappApiChannel
        ? this.$t('INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.DESC')
        : this.$t('INBOX_MGMT.ADD.API_CHANNEL.DESC');
    },
    channelNameLabel() {
      return this.isWhatsappApiChannel
        ? this.$t('INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.CHANNEL_NAME.LABEL')
        : this.$t('INBOX_MGMT.ADD.API_CHANNEL.CHANNEL_NAME.LABEL');
    },
    channelNamePlaceholder() {
      return this.isWhatsappApiChannel
        ? this.$t(
            'INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.CHANNEL_NAME.PLACEHOLDER'
          )
        : this.$t('INBOX_MGMT.ADD.API_CHANNEL.CHANNEL_NAME.PLACEHOLDER');
    },
    channelNameError() {
      return this.isWhatsappApiChannel
        ? this.$t('INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.CHANNEL_NAME.ERROR')
        : this.$t('INBOX_MGMT.ADD.API_CHANNEL.CHANNEL_NAME.ERROR');
    },
    webhookLabel() {
      return this.isWhatsappApiChannel
        ? this.$t('INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.WEBHOOK_URL.LABEL')
        : this.$t('INBOX_MGMT.ADD.API_CHANNEL.WEBHOOK_URL.LABEL');
    },
    webhookPlaceholder() {
      return this.isWhatsappApiChannel
        ? this.$t('INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.WEBHOOK_URL.PLACEHOLDER')
        : this.$t('INBOX_MGMT.ADD.API_CHANNEL.WEBHOOK_URL.PLACEHOLDER');
    },
    webhookSubtitle() {
      return this.isWhatsappApiChannel
        ? this.$t('INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.WEBHOOK_URL.SUBTITLE')
        : this.$t('INBOX_MGMT.ADD.API_CHANNEL.WEBHOOK_URL.SUBTITLE');
    },
    submitButtonLabel() {
      return this.isWhatsappApiChannel
        ? this.$t('INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.SUBMIT_BUTTON')
        : this.$t('INBOX_MGMT.ADD.API_CHANNEL.SUBMIT_BUTTON');
    },
    channelErrorMessage() {
      return this.isWhatsappApiChannel
        ? this.$t('INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.API.ERROR_MESSAGE')
        : this.$t('INBOX_MGMT.ADD.API_CHANNEL.API.ERROR_MESSAGE');
    },
  },
  validations: {
    channelName: { required },
    webhookUrl: { shouldBeWebhookUrl },
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      if (this.isWhatsappApiChannel) {
        await this.startWhatsappApiSetup();
        return;
      }

      try {
        const apiChannel = await this.$store.dispatch('inboxes/createChannel', {
          name: this.channelName?.trim(),
          channel: {
            type: this.channelType,
            webhook_url: this.webhookUrl,
          },
        });

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: apiChannel.id,
          },
        });
      } catch (error) {
        const errorMessage = error.message || this.channelErrorMessage;
        useAlert(errorMessage);
      }
    },
    async startWhatsappApiSetup() {
      try {
        const response = await this.$store.dispatch(
          'inboxes/startWhatsappApiSetup',
          {
            name: this.channelName?.trim(),
          }
        );
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: response.inbox.id,
          },
        });
      } catch (error) {
        const errorMessage = error.message || this.channelErrorMessage;
        useAlert(errorMessage);
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="headerTitle"
      :header-content="headerDescription"
    />
    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.channelName.$error }">
          {{ channelNameLabel }}
          <input
            v-model="channelName"
            type="text"
            :placeholder="channelNamePlaceholder"
            @blur="v$.channelName.$touch"
          />
          <span v-if="v$.channelName.$error" class="message">{{
            channelNameError
          }}</span>
        </label>
      </div>

      <div v-if="!isWhatsappApiChannel" class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.webhookUrl.$error }">
          {{ webhookLabel }}
          <input
            v-model="webhookUrl"
            type="text"
            :placeholder="webhookPlaceholder"
            @blur="v$.webhookUrl.$touch"
          />
        </label>
        <p class="help-text">
          {{ webhookSubtitle }}
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="submitButtonLabel"
        />
      </div>
    </form>
  </div>
</template>
